/**
 * Tripo AI API Service
 * 
 * Documentation: https://docs.tripo.ai/
 * 
 * This service handles:
 * - Image to 3D model generation
 * - Task status polling
 * - Error handling
 */

const TRIPO_BASE_URL = 'https://api.tripo.ai/v2';
const TRIPO_API_KEY = import.meta.env.VITE_TRIPO_API_KEY;

if (!TRIPO_API_KEY) {
  console.warn('[TripoAI] VITE_TRIPO_API_KEY is not set. Some features may not work.');
}

export interface TripoTaskResponse {
  code: number;
  data: {
    task_id: string;
    status: 'running' | 'success' | 'failed';
    output?: {
      model?: string;
      preview_image?: string;
    };
    error?: string;
  };
  message?: string;
}

export interface TripoModelResult {
  modelUrl: string;
  previewImageUrl?: string;
  taskId: string;
}

/**
 * Generate 3D model from image
 * 
 * @param imageUrl - URL of the image to convert
 * @param quality - Quality level (default: medium)
 * @returns Task ID for polling
 */
export async function generate3DModel(
  imageUrl: string,
  quality: 'low' | 'medium' | 'high' = 'medium'
): Promise<string> {
  if (!TRIPO_API_KEY) {
    throw new Error('Tripo AI API key is not configured');
  }

  const response = await fetch(`${TRIPO_BASE_URL}/generation/image2model`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${TRIPO_API_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      image_url: imageUrl,
      quality,
      format: 'glb',
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Tripo API error: ${response.status} - ${error}`);
  }

  const data: TripoTaskResponse = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || 'Failed to start 3D generation');
  }

  return data.data.task_id;
}

/**
 * Poll task status until completion
 * 
 * @param taskId - Task ID from generate3DModel
 * @param onProgress - Optional progress callback
 * @param timeoutMs - Timeout in milliseconds (default: 120000 = 2 minutes)
 * @returns Model result with URLs
 */
export async function pollTaskStatus(
  taskId: string,
  onProgress?: (status: string, progress: number) => void,
  timeoutMs: number = 120000
): Promise<TripoModelResult> {
  if (!TRIPO_API_KEY) {
    throw new Error('Tripo AI API key is not configured');
  }

  const startTime = Date.now();
  const pollInterval = 2000; // 2 seconds
  let attempts = 0;
  const maxAttempts = timeoutMs / pollInterval;

  while (attempts < maxAttempts) {
    const elapsed = Date.now() - startTime;
    
    if (elapsed >= timeoutMs) {
      throw new Error('Task timeout - 3D generation took too long');
    }

    await new Promise(resolve => setTimeout(resolve, pollInterval));

    const response = await fetch(`${TRIPO_BASE_URL}/generation/${taskId}`, {
      headers: {
        'Authorization': `Bearer ${TRIPO_API_KEY}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to poll task status: ${response.status}`);
    }

    const data: TripoTaskResponse = await response.json();

    if (data.code !== 0) {
      throw new Error(data.message || 'Task failed');
    }

    const status = data.data.status;
    const progress = Math.min((elapsed / timeoutMs) * 100, 99);

    if (onProgress) {
      onProgress(status, progress);
    }

    if (status === 'success') {
      if (!data.data.output?.model) {
        throw new Error('Task completed but no model URL returned');
      }

      return {
        modelUrl: data.data.output.model,
        previewImageUrl: data.data.output.preview_image,
        taskId,
      };
    }

    if (status === 'failed') {
      throw new Error(data.data.error || '3D generation failed');
    }

    // status === 'running'
    attempts++;
  }

  throw new Error('Task polling exceeded maximum attempts');
}

/**
 * Complete flow: generate model and wait for completion
 * 
 * @param imageUrl - URL of the image to convert
 * @param onProgress - Optional progress callback
 * @returns Model result with URLs
 */
export async function generate3DModelComplete(
  imageUrl: string,
  onProgress?: (status: string, progress: number) => void
): Promise<TripoModelResult> {
  console.log('[TripoAI] Starting 3D generation for:', imageUrl);
  
  if (onProgress) {
    onProgress('Starting...', 0);
  }

  const taskId = await generate3DModel(imageUrl);
  console.log('[TripoAI] Task started:', taskId);

  if (onProgress) {
    onProgress('Generating 3D model...', 10);
  }

  const result = await pollTaskStatus(taskId, onProgress);
  console.log('[TripoAI] Task completed:', result);

  if (onProgress) {
    onProgress('Complete!', 100);
  }

  return result;
}

/**
 * Convert local file to base64 data URL
 * 
 * @param file - Image file to convert
 * @returns Base64 data URL
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Upload file to temporary hosting (for Tripo AI)
 * 
 * Note: Tripo AI requires a publicly accessible URL.
 * For development, you can use services like:
 * - imgbb.com
 * - cloudinary.com
 * - Your own S3 bucket
 * 
 * For production, implement proper file upload to your storage.
 * 
 * @param file - Image file to upload
 * @returns Public URL of uploaded file
 */
export async function uploadImageForTripo(file: File): Promise<string> {
  // TODO: Implement proper file upload to your storage service
  // For now, return a data URL (works for small files in development)
  return await fileToDataUrl(file);
  
  // Production example:
  // const formData = new FormData();
  // formData.append('file', file);
  // const response = await fetch('/api/upload', {
  //   method: 'POST',
  //   body: formData,
  // });
  // const data = await response.json();
  // return data.url;
}
